/* The terms of service, at /terms, linked from the legal page and from Stripe's account settings.
 *
 * The subscription section restates Tim's rules (docs es-subscribe.md; EsSubService and
 * EsStripeClient carry them): $35 a year plus sales tax added at checkout, a 30-day trial,
 * auto-renew off unless ticked, no refunds, and a lifetime account's payment is a donation.
 * Change the rule and this page in the same diff. Not legal advice; Tim reviews the wording.
 *
 * The home page's Principles bind this page: ownership of their work, easy export, irrevocable
 * terms of use, and fair pricing. A clause that takes one back is the clause that changes.
 */

import { MIN_AGE, VIEW_ACCOUNT, VIEW_HOME, VIEW_LEGAL, VIEW_PRIVACY } from '../../common/EsConstants';
import EsPolicy, { BUSINESS_NAME, EsList, EsMail, EsP, EsTo } from './EsPolicy';
import { esLiteral, useEsStrings } from '../../common/EsStrings';

export const termsSections = ( t ) => [
    {
        key: 'agreement',
        heading: t( 'legal-terms-agreement-heading', 'Agreement' ),
        body: <>
            <EsP>
                { t( 'legal-terms-escapade-is-run-by', 'Escapade is run by' ) } { BUSINESS_NAME } { t( 'legal-terms-we-by-using-escapade', '(“we”). By using escapade.gg you agree to these terms and to the' ) } <EsTo to={ VIEW_PRIVACY }>{ t( 'legal-terms-privacy-policy', 'privacy policy' ) }</EsTo>.
            </EsP>
        </>,
    },
    {
        // Moved here from the legal page, word for word (COPPA; Tim, 2026-10-14).
        key: 'age',
        heading: t( 'legal-terms-age-heading', 'Age' ),
        body: <>
            <EsP>
                { t( 'legal-terms-you-must-be', 'You must be' ) } { MIN_AGE } { t( 'legal-terms-or-older-to-use', 'or older to use Escapade. You are responsible for anyone who uses your account. A child under' ) } { MIN_AGE } { t( 'legal-terms-may-play-only-on', 'may play only on a parent\'s or guardian\'s account, with them supervising. If you find an account that belongs to a child under' ) } { MIN_AGE }{ t( 'legal-terms-tell-us-with-the', ', tell us with the contact form on the home page and we will delete it.' ) }
            </EsP>
        </>,
    },
    {
        key: 'accounts',
        heading: t( 'legal-terms-accounts-heading', 'Your account' ),
        body: <>
            <EsP>
                { t( 'legal-terms-give-a-real-email', 'Give a real email address and keep your password to yourself. You are responsible for what is done with your account. Tell us at once if you think someone else is using it.' ) }
            </EsP>
        </>,
    },
    {
        key: 'content',
        heading: t( 'legal-terms-content-heading', 'Your content' ),
        body: <>
            <EsP>
                { t( 'legal-terms-what-you-make-in', 'What you make in Escapade stays yours: characters, campaigns, chat, maps, packs, and the rest. You give us a worldwide, royalty-free licence to store, copy, and show it, only as far as running Escapade needs: to you, to the tables you share it with, and to the Library when you publish a pack there. The licence ends when the content is deleted, except for chat lines, which stay in their campaigns, and backups, which expire.' ) }
            </EsP>
            <EsP>
                { t( 'legal-terms-you-may-take-your', 'You may take your content elsewhere whenever you like. Until Escapade has an export of its own, write to us and we will send you a copy of what you made.' ) }
            </EsP>
            <EsP>
                { t( 'legal-terms-you-promise-you-have', 'You promise you have the right to put it on Escapade.' ) }
            </EsP>
        </>,
    },
    {
        // The legal page's Packs notice, moved here word for word (es-todo 1, V062).
        key: 'packs',
        heading: t( 'legal-terms-packs-heading', 'Packs and the Library' ),
        body: <>
            <EsP>
                { t( 'legal-terms-a-pack-is-its', 'A pack is its owner\'s work, not Escapade\'s, and Escapade does not pre-screen what anyone writes into one. An owner confirms, each time they publish a pack to the Library, that it holds no copyrighted text they do not have the rights to publish. An admin approves a pack before the Library shows it; that approval is not a review of its rights.' ) }
            </EsP>
            <EsP>
                { t( 'legal-terms-a-pack-published-to', 'A pack published to the Library may be installed and used by anyone on Escapade. We may take a pack out of the Library, or off Escapade, when it breaks these terms or someone else\'s rights.' ) }
            </EsP>
        </>,
    },
    {
        key: 'ours',
        heading: t( 'legal-terms-ours-heading', 'Our content' ),
        body: <>
            <EsP>
                { t( 'legal-terms-escapade-s-software-design', 'Escapade\'s software, design, and writing are ours. Use them on escapade.gg; do not copy, resell, or rebuild them elsewhere. Some packs hold content licensed to us by others, shown only to the accounts entitled to it; it stays inside Escapade.' ) }
            </EsP>
            <EsP>
                { t( 'legal-terms-open-content-is-the', 'Open content is the exception. The System Reference Document 5.2.1 text in the codex is licensed under Creative Commons Attribution 4.0, and you may copy it under that licence. The' ) } <EsTo to={ VIEW_LEGAL }>{ t( 'legal-terms-legal-page', 'legal page' ) }</EsTo> { t( 'legal-terms-carries-its-attribution-and', 'carries its attribution and the other credits.' ) }
            </EsP>
        </>,
    },
    {
        key: 'use',
        heading: t( 'legal-terms-use-heading', 'Acceptable use' ),
        body: <>
            <EsP>{ t( 'legal-terms-do-not-use-escapade', 'Do not use Escapade to:' ) }</EsP>
            <EsList>
                <li>{ t( 'legal-terms-break-the-law-or', 'break the law, or post what you have no right to post;' ) }</li>
                <li>{ t( 'legal-terms-harass-threaten-or-impersonate', 'harass, threaten, or impersonate anyone;' ) }</li>
                <li>{ t( 'legal-terms-post-sexual-content-involving', 'post sexual content involving minors, or content that promotes violence or hatred;' ) }</li>
                <li>{ t( 'legal-terms-send-spam-or-collect', 'send spam, or collect other people\'s information;' ) }</li>
                <li>{ t( 'legal-terms-break-into-probe-overload', 'break into, probe, overload, or scrape the site, or get round its limits;' ) }</li>
                <li>{ t( 'legal-terms-sell-or-rent-access', 'sell or rent access to your account.' ) }</li>
            </EsList>
            <EsP>
                { t( 'legal-terms-screening', 'Escapade screens what people write and hides hate speech on its own. Moderators read reports, and may hide a post or suspend an account that breaks these rules.' ) }
            </EsP>
            <EsP>
                { t( 'legal-terms-to-report-content-that', 'To report content that infringes your rights or breaks these rules, write to' ) } <EsMail />.
            </EsP>
        </>,
    },
    {
        key: 'subscriptions',
        heading: t( 'legal-terms-subscriptions-heading', 'Subscriptions' ),
        body: <>
            <EsList>
                <li><b>{ t( 'legal-terms-what-it-covers', 'What it covers.' ) }</b> { t( 'legal-terms-a-subscription-lets-you', 'A subscription lets you create campaigns, run sessions, and write packs. Players never pay: joining a table, playing, chatting, and reading the codex are free.' ) }</li>
                <li><b>{ t( 'legal-terms-trial', 'Trial.' ) }</b> { t( 'legal-terms-a-new-account-gets', 'A new account gets 30 days free, with no card asked for. The trial ends at midnight Eastern time.' ) }</li>
                <li><b>{ t( 'legal-terms-price', 'Price.' ) }</b> { t( 'legal-terms-35-us-dollars-for', '$35 (US dollars) for one year, plus any sales tax that applies, added at checkout. Stripe takes the payment; we never see your card.' ) }</li>
                <li><b>{ t( 'legal-terms-renewing-early', 'Renewing early.' ) }</b> { t( 'legal-terms-a-payment-adds-a', 'A payment adds a year to the end date you already have, so renewing early loses nothing.' ) }</li>
                <li><b>{ t( 'legal-terms-auto-renew', 'Auto-renew.' ) }</b> { t( 'legal-terms-off-unless-you-tick', 'Off unless you tick the box at checkout. With it on, Stripe charges $35 plus tax each year until you turn it off on your' ) } <EsTo to={ VIEW_ACCOUNT }>{ t( 'legal-terms-account-page', 'account page' ) }</EsTo>{ t( 'legal-terms-turning-it-off-stops', '. Turning it off stops the next charge; the year you paid for runs to its end.' ) }</li>
                <li><b>{ t( 'legal-terms-no-refunds', 'No refunds.' ) }</b> { t( 'legal-terms-the-trial-is-the', 'The trial is the try-out. Every payment is final, an automatic renewal included.' ) }</li>
                <li><b>{ t( 'legal-terms-when-it-ends', 'When it ends.' ) }</b> { t( 'legal-terms-you-keep-your-account', 'You keep your account and everything in it, and you can still play and read. Creating campaigns, starting sessions, and writing packs wait until you renew.' ) }</li>
            </EsList>
        </>,
    },
    {
        key: 'donations',
        heading: t( 'legal-terms-donations-heading', 'Donations' ),
        body: <>
            <EsP>
                { t( 'legal-terms-alpha-beta-and-administrator', 'Alpha, Beta, and administrator accounts are free for life. A payment from one of them is a donation: it buys nothing, it is not refunded, and it can repeat each year if you tick the box at checkout. Donations are not tax-deductible.' ) }
            </EsP>
        </>,
    },
    {
        key: 'termination',
        heading: t( 'legal-terms-termination-heading', 'Ending your account' ),
        body: <>
            <EsP>
                { t( 'legal-terms-you-may-stop-using', 'You may stop using Escapade at any time, and we delete your account when you ask. We may suspend or delete an account that breaks these terms; that gives no refund. If we ever close Escapade, we will tell every account by email first and give you time to take your content with you.' ) }
            </EsP>
        </>,
    },
    {
        key: 'disclaimers',
        heading: t( 'legal-terms-disclaimers-heading', 'Disclaimers' ),
        body: <>
            <EsP>
                { t( 'legal-terms-escapade-is-provided-as', 'Escapade is provided as it is and as available, without warranties of any kind, to the extent the law allows. We do not promise it will always be up, free of faults, or that content will never be lost. Keep your own copy of anything you cannot afford to lose. Content written by other accounts is theirs, not ours.' ) }
            </EsP>
        </>,
    },
    {
        key: 'liability',
        heading: t( 'legal-terms-liability-heading', 'Liability' ),
        body: <>
            <EsP>
                { t( 'legal-terms-to-the-extent-the', 'To the extent the law allows, we are not liable for indirect or consequential loss, lost data, or lost profits. Our total liability to you is no more than you paid us in the 12 months before the claim.' ) }
            </EsP>
        </>,
    },
    {
        key: 'law',
        heading: t( 'legal-terms-law-heading', 'Governing law' ),
        body: <>
            <EsP>
                { t( 'legal-terms-the-laws-of-the', 'The laws of the Commonwealth of Pennsylvania govern these terms, and any dispute belongs in the courts of Pennsylvania.' ) }
            </EsP>
        </>,
    },
    {
        key: 'changes',
        heading: t( 'legal-terms-changes-heading', 'Changes' ),
        body: <>
            <EsP>
                { t( 'legal-terms-we-post-any-change', 'We post any change here with a new effective date. A change never takes back a right these terms already give you: your ownership of your work, your licence to it, taking it elsewhere, and the terms a pack was published under all stay as they were.' ) }
            </EsP>
        </>,
    },
    {
        key: 'contact',
        heading: t( 'legal-terms-contact-heading', 'Contact' ),
        body: <>
            <EsP>
                { BUSINESS_NAME }, <EsMail />{ t( 'legal-terms-or-the-contact-form', ', or the contact form on the' ) } <EsTo to={ VIEW_HOME }>{ t( 'legal-terms-home-page', 'home page' ) }</EsTo>.
            </EsP>
        </>,
    },
];

// The English, for a test or a caller with no hook; the page builds its own in the reader's locale.
export const TERMS_SECTIONS = termsSections( esLiteral );

export default function EsTerms() {
    const t = useEsStrings();
    return <EsPolicy id="es-terms" title={ t( 'legal-terms-title', 'Terms of Service' ) } sections={ termsSections( t ) } />;
}
